// Testimonials output: the public landing-page section (approved only) and the admin review panel.
import { h, toast } from './h.js';
import { summarizeTestimonials, isPublishable } from '../core/testimonials.js';

const day = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return ''; } };

/**
 * Landing page "What learners say". Stays hidden until at least one approved testimonial
 * loads — RouteZero never shows placeholder or invented quotes.
 */
export function testimonialsSection(service) {
  const list = h('div', { class: 'rz-quotes', 'data-testid': 'landing-testimonials-list' });
  const section = h('section', { class: 'rz-section rz-quotes-section', id: 'rz-testimonials', 'aria-labelledby': 'rz-quotes-title', 'data-testid': 'landing-testimonials', hidden: true },
    h('div', { class: 'rz-sec-head' }, h('div', {},
      h('span', { class: 'rz-eyebrow' }, 'FROM OUR LEARNERS'),
      h('h2', { id: 'rz-quotes-title' }, 'What learners say'),
      h('p', {}, 'Shared by RouteZero learners from their profile, with their permission.'))),
    list);
  if (service) {
    service.published().then((rows) => {
      if (!rows?.length) return;
      list.append(...rows.map((r) => h('figure', { class: 'panel rz-quote', 'data-testid': 'landing-testimonial' },
        h('blockquote', {}, h('p', {}, `“${r.body}”`)),
        h('figcaption', {}, h('b', {}, r.display_name || 'RouteZero learner'), h('span', {}, 'RouteZero learner')))));
      section.hidden = false;
    }).catch(() => {});
  }
  return section;
}

/** Admin dashboard: recommend counter + every testimonial with Approve / Reject. */
export function adminTestimonialsPanel(service) {
  const body = h('div', { class: 'rz-tm-admin-body' }, h('p', { class: 'muted small' }, 'Loading testimonials…'));
  const panel = h('section', { class: 'panel admin-card rz-tm-admin', 'data-testid': 'admin-testimonials' },
    h('div', { class: 'rc-title' }, 'TESTIMONIALS · WOULD YOU RECOMMEND ROUTEZERO?'),
    service?.mode === 'local' ? h('p', { class: 'small muted', 'data-testid': 'admin-testimonials-dev' }, 'Development mode: these come from learners who signed in on this browser (no Supabase connected).') : null,
    body);
  const load = async () => {
    let rows;
    try { rows = await service.listAll(); } catch (e) {
      body.replaceChildren(h('p', { class: 'small', role: 'alert' }, `Could not load testimonials: ${e.message}. Has supabase/migrations/0002_testimonials.sql been run?`));
      return;
    }
    const s = summarizeTestimonials(rows);
    const stat = (label, value, id) => h('div', { class: 'rz-tm-stat', 'data-testid': `admin-testimonials-${id}` }, h('b', {}, String(value)), h('span', {}, label));
    const counter = h('div', { class: 'rz-tm-stats' },
      stat('would recommend', s.recommendPct == null ? '—' : `${s.recommendPct}%`, 'pct'),
      stat('👍 yes', s.yes, 'yes'), stat('👎 no', s.no, 'no'),
      stat('waiting for review', s.pending, 'pending'), stat('on the website', s.published, 'published'));
    const act = (r, status, label, cls = 'ghost') => h('button', {
      class: `btn small ${cls}`, type: 'button', 'data-testid': `admin-testimonial-${status}`,
      disabled: (status === 'approved' && !isPublishable(r)) || r.status === status ? true : null,
      title: status === 'approved' && !isPublishable(r) ? 'Only 👍 testimonials with text and the learner’s permission can be published' : null,
      onclick: async () => { const res = await service.setStatus(r.id, status); toast(res.ok ? `Testimonial ${status === 'approved' ? 'approved' : status === 'rejected' ? 'rejected' : 'moved back to review'}` : res.error, res.ok ? 'info' : 'error'); load(); },
    }, label);
    const items = rows.length ? h('ul', { class: 'rz-tm-admin-list' }, rows.map((r) => h('li', { class: `rz-tm-admin-item ${r.status}`, 'data-testid': 'admin-testimonial', 'data-status': r.status },
      h('div', { class: 'rz-tm-admin-head' },
        h('span', { class: 'rz-tm-vote', 'aria-label': r.vote === 1 ? 'Would recommend' : 'Would not recommend' }, r.vote === 1 ? '👍' : '👎'),
        h('b', {}, r.display_name || '(no name)'),
        h('span', { class: `chip ${r.status === 'approved' ? 'complete' : r.status === 'rejected' ? 'locked' : 'available'}` }, r.status.toUpperCase()),
        h('span', { class: 'small muted' }, `${r.consent ? 'May be published' : 'Private feedback'} · ${day(r.updated_at)}`)),
      r.body ? h('p', { class: 'rz-tm-admin-text' }, r.body) : h('p', { class: 'small muted' }, 'Vote only — no text.'),
      h('div', { class: 'row' }, act(r, 'approved', 'Approve', 'primary'), act(r, 'rejected', 'Reject'), act(r, 'pending', 'Back to review')))))
      : h('p', { class: 'muted small', 'data-testid': 'admin-testimonials-empty' }, 'No testimonials yet. Learners can share one from their Profile page.');
    body.replaceChildren(counter, items);
  };
  if (service) load(); else body.replaceChildren(h('p', { class: 'muted small' }, 'Testimonials are not available.'));
  return panel;
}

/**
 * Sign-up page: one real, approved testimonial under the form ("From our learners").
 * Stays hidden when there is none — never a placeholder quote. With several, "Next" shows another.
 */
export function signupTestimonial(service) {
  const quote = h('blockquote', {}, h('p', {}));
  const who = h('figcaption', {});
  const next = h('button', { type: 'button', class: 'rz-auth-tm-next', 'data-testid': 'signup-testimonial-next', hidden: true }, 'Next');
  const box = h('figure', { class: 'rz-auth-tm', 'data-testid': 'signup-testimonial', hidden: true, 'aria-label': 'What a RouteZero learner says', 'aria-live': 'polite' },
    h('div', { class: 'rz-auth-tm-head' }, h('span', { class: 'rz-auth-tm-k' }, 'FROM OUR LEARNERS'), next), quote, who);
  if (!service) return box;
  let rows = []; let i = 0;
  const show = () => {
    const r = rows[i % rows.length];
    quote.firstChild.textContent = `“${r.body}”`;
    who.replaceChildren(h('b', {}, r.display_name || 'RouteZero learner'), h('span', {}, ' · RouteZero learner'));
    next.setAttribute('aria-label', `Show another testimonial (${(i % rows.length) + 1} of ${rows.length})`);
  };
  next.addEventListener('click', () => { i++; show(); });
  service.published().then((list) => {
    rows = (list || []).filter((r) => r && r.body);
    if (!rows.length) return;
    show(); box.hidden = false; next.hidden = rows.length < 2;
  }).catch(() => {});
  return box;
}
